/**
 * Public Signal Export V1 - Export Validator
 * 
 * Validates export inputs before generating export.
 * 
 * BOUNDARIES:
 * - Advisory only: no execution authority
 * - Deterministic output only
 * - Fail closed on invalid inputs
 * - No mutation of inputs
 */

import { PublicSignalDraftV1 } from '../publicSignalDraft';
import { PublicSignalApprovalReceiptV1 } from '../publicSignalApproval';
import { verifyReceiptIntegrity } from '../publicSignalApproval/publicSignalApprovalReceipt';
import { containsForbiddenTerms } from '../publicSignalDraft/forbiddenTermScanner';
import { ExportValidationResult } from './types';

/**
 * Validate export inputs
 */
export function validateExport(
  draft: PublicSignalDraftV1,
  receipt: PublicSignalApprovalReceiptV1
): ExportValidationResult {
  // Verify receipt integrity
  if (!verifyReceiptIntegrity(receipt)) {
    return {
      valid: false,
      reason: 'Receipt integrity verification failed',
    };
  }

  // Verify sourceDraftHash matches draft
  if (receipt.sourceDraftHash !== draft.draftHash) {
    return {
      valid: false,
      reason: 'Source draft hash does not match receipt',
    };
  }

  // Verify decision is approved
  if (receipt.decision !== 'approved') {
    return {
      valid: false,
      reason: `Receipt decision is ${receipt.decision}, only approved receipts can be exported`,
    };
  }

  // Verify eligibleForPublishing is true
  if (!receipt.eligibleForPublishing) {
    return {
      valid: false,
      reason: 'Receipt is not eligible for publishing',
    };
  }

  // Determine final text (use edited text if draft was edited)
  const finalText = receipt.edited ? 
    (receipt as any).editedDraftText || draft.draftText : 
    draft.draftText;

  // Scan final text for forbidden terms
  if (containsForbiddenTerms(finalText)) {
    return {
      valid: false,
      reason: 'Final text contains forbidden terms',
    };
  }

  return {
    valid: true,
  };
}
